import { ChangeDetectionStrategy, Component, computed, inject, input, Signal, untracked, WritableSignal } from '@angular/core';
import { VisualEditorComponent } from 'src/app/pivot-studio/components/visual-editor/VisualEditor.component';
import { Visual } from '../Visual';
import { FontSize, VisualStyle } from '../VisualStyle';
import { FontSizeOptions } from './TextElementStyleEditor.component';

// TODO: Move out
export type TypeOfValue<T, K extends keyof T> = T[K];

@Component({
  selector: 'pbir-visual-style-editor',
  templateUrl: './VisualStyleEditor.component.html',
  styles: [`
    .visual-style-editor__subtitle-link {
      .text {
        text-decoration: underline;
      }

      cursor: pointer;

      &.disabled {
        cursor: default;
        color: #979797;
      }
    }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class VisualStyleEditorComponent<
  TOptions extends Visual.Options = Visual.Options,
  TState extends Visual.State = Visual.State,
  TStyle extends VisualStyle = VisualStyle> {
  readonly FontSizeOptions = FontSizeOptions;

  readonly busy = input<boolean>(false);
  readonly visual = input.required<Visual<TOptions, TState, TStyle>>();

  get options(): Signal<TOptions> { return this.visual().options; }
  get state(): Signal<TState> { return this.visual().state; }
  get style(): Signal<TStyle> { return this.visual().style; }

  readonly hasBackground = computed(() => this.visual().type.hasBackground);
  get backgroundColor() { return this.style().backgroundColor; }

  setBackgroundColor(value: string) {
    this.setValue(this.style().backgroundColor, value);
  }

  get title() { return this.style().title; }
  get subtitle() { return this.style().subtitle; }

  // TODO: Abstract away
  readonly visualEditor = inject(VisualEditorComponent);

  setValue<T>(signal: WritableSignal<T>, newValue: T) {
    untracked(() => {
      if (newValue === signal())
        return;

      signal.set(newValue);
      this.visualEditor?.onVisualChange({ userAction: true });
    });
  }

  updateValue<T>(signal: WritableSignal<T>, factory: (x: T) => T) {
    untracked(() => {
      let value = signal();
      let newValue = factory(value);
      this.setValue(signal, newValue);
    });
  }

  readonly titleEnabled = computed(() => this.style()?.title()?.enabled);

  onTitleChanged() {
    let style = this.style();
    let titleSize = style?.title()?.fontSize;
    let subTitleSize = FontSize.value(titleSize, 14) - 2;

    style.subtitle.set({
      ...style?.subtitle(),
      fontSize: {
        value: subTitleSize,
        kind: 'px'
      }
    });
  }

  subtitleLinkClick() {
    if (!this.titleEnabled())
      return;

    this.visualEditor.openSettingsClick();
  }
}
